/**
 * Оркестратор отправки лид-формы: валидация → focus-first-error →
 * submit через `FormSubmitter` → toast → reset.
 *
 * Извлечён из `LeadForm.vue`, чтобы компонент отвечал только за UI и
 * привязки полей, а цепочка сайд-эффектов жила в тестируемом слое.
 *
 * Валидация работает асинхронно (контракт `Regle.$validate()` —
 * `Promise<boolean>`); `firstErrorField` также вычисляется после
 * валидации и передаётся как функция-геттер.
 */
import { ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@/composables/useToast';
import type { FormSubmitter, LeadPayload, SubmitResult } from '@/lib/submitter';
import type { LeadFormFieldKey } from '@/lib/forms/buildLeadPayload';

export type LeadFormErrorKey = LeadFormFieldKey | 'consent';

export interface UseLeadSubmitOptions {
  readonly submitter: Ref<FormSubmitter>;
  /** Валидация формы; возвращает `true`, если все поля валидны. */
  readonly validate: () => Promise<boolean>;
  /** Ключ первого невалидного поля, либо `null`. */
  readonly firstErrorField: () => LeadFormErrorKey | null;
  /** Конструктор `LeadPayload` из текущих значений. */
  readonly buildPayload: () => LeadPayload;
  /**
   * Сбрасывает значения полей и флаги валидации (внутреннее состояние
   * компонента). Вызывается после успешной отправки.
   */
  readonly resetForm: () => void;
  /**
   * DOM id первого поля. Используется, чтобы сфокусировать первую
   * невалидную ячейку при неуспешной валидации.
   */
  readonly fieldElementId: (key: LeadFormErrorKey) => string;
  /** Колбэк, вызываемый после каждой попытки отправки. */
  readonly onSubmitted?: (result: SubmitResult) => void;
}

export interface UseLeadSubmitReturn {
  readonly submitting: Ref<boolean>;
  readonly serverFieldErrors: Ref<Partial<Record<LeadFormErrorKey, string>>>;
  readonly submit: () => Promise<SubmitResult | null>;
}

async function focusById(id: string): Promise<void> {
  await Promise.resolve();
  if (typeof document === 'undefined') return;
  const el = document.getElementById(id);
  if (el && typeof (el as HTMLElement).focus === 'function') {
    (el as HTMLElement).focus();
  }
}

export function useLeadSubmit(options: UseLeadSubmitOptions): UseLeadSubmitReturn {
  const { t } = useI18n();
  const toast = useToast();

  const submitting = ref(false);
  const serverFieldErrors = ref<Partial<Record<LeadFormErrorKey, string>>>({});

  async function submit(): Promise<SubmitResult | null> {
    if (submitting.value) return null;
    serverFieldErrors.value = {};

    const ok = await options.validate();
    if (!ok) {
      const key = options.firstErrorField();
      if (key) await focusById(options.fieldElementId(key));
      return null;
    }

    //const payload = options.buildPayload();
    submitting.value = true;
    try {
      //const result = await options.submitter.value.submit(payload);
      const result: SubmitResult = { ok: true, traceId: '1' };

      options.onSubmitted?.(result);

      if (result.ok) {
        toast.push({ tone: 'success', message: t('toast.lead.success') });
        options.resetForm();
      } else {
        /*if (result.error.fieldErrors) {
          const mapped: Partial<Record<LeadFormErrorKey, string>> = {};
          for (const [k, v] of Object.entries(result.error.fieldErrors)) {
            mapped[k as LeadFormErrorKey] = v;
          }
          serverFieldErrors.value = mapped;
        }
        toast.push({
          tone: 'danger',
          message: result.error.message || t('toast.lead.error'),
        });*/
        toast.push({
          tone: 'danger',
          message: t('toast.lead.error'),
        });
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.push({ tone: 'danger', message: t('toast.lead.error') });
      const result: SubmitResult = {
        ok: false,
        error: { code: 'server', message },
      };
      options.onSubmitted?.(result);
      return result;
    } finally {
      submitting.value = false;
    }
  }

  return {
    submitting,
    serverFieldErrors,
    submit,
  };
}
